'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CaseStudyDownloadModal from '@/components/CaseStudyDownloadModal';

const cobalt = 'rgb(43, 98, 239)';
const cobaltGlow = 'rgb(85, 136, 251)';
const silverText = 'oklch(95% .005 265)';
const silverMuted = 'oklch(65% .01 265)';
const obsidian = 'rgb(8, 11, 18)';

function ArrowRightIcon({ size = 16 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5" style={{ color: cobalt }}>
      <circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" />
    </svg>
  );
}

const stats = [
  { label: 'Post-Editing Accuracy Rate', value: '99.7%', sub: 'Achieved across all life sciences projects in the last 12 months.' },
  { label: 'Specialist Medical Linguists', value: '1,200+', sub: 'Covering 75+ languages with expertise in various therapeutic areas.' },
  { label: 'Turnaround for Key Documents', value: '48-Hour', sub: 'Average delivery time for critical-path regulatory updates under 2,000 words.' },
  { label: 'APAC Markets Served', value: '15+', sub: 'Deep localization expertise for regulatory bodies across Asia.' },
];

const deliverables = [
  { title: 'ISO-Compliant QA Pipeline', desc: 'Our quality management system is certified to ISO 9001 and ISO 17100 standards, with ISO 27001 in progress, ensuring every translation undergoes a rigorous, multi-step verification process for accuracy and compliance.' },
  { title: 'Domain-Specialist Translators', desc: 'Your content is handled by a curated network of professional linguists with advanced degrees and extensive experience in medicine, pharmacology, and medical device technology.' },
  { title: 'APAC Regulatory Awareness', desc: 'Navigate the complex regulatory landscape of Asia-Pacific with confidence. Our teams in Hong Kong, Beijing, Shanghai, Bangalore, and Tokyo provide up-to-date insights on local requirements.' },
  { title: 'AI-Powered Efficiency', desc: 'We leverage customized neural machine translation engines trained on medical-specific corpora, which are then meticulously reviewed and refined by human experts to accelerate timelines and reduce costs.' },
  { title: 'Comprehensive Document Support', desc: 'From clinical trial protocols and informed consent forms (ICFs) to marketing materials and medical device manuals, we handle a full spectrum of life sciences documentation.' },
  { title: 'Patient-Centric Approach', desc: 'We ensure that patient-facing materials, such as educational content and package inserts, are translated with cultural sensitivity and clarity to improve health literacy and patient outcomes.' },
];

const steps = [
  { num: '01', title: 'Project & Terminology Setup', desc: 'We analyze your source documents, establish a dedicated project team, and create a client-specific termbase and translation memory to ensure consistency.' },
  { num: '02', title: 'AI-Assisted Translation', desc: 'Our custom-trained neural machine translation engines perform the initial translation, providing a high-quality baseline for our human experts.' },
  { num: '03', title: 'Human Post-Editing & Review', desc: 'A subject matter expert linguist meticulously reviews and refines the AI output, ensuring clinical accuracy, correct terminology, and natural phrasing.' },
  { num: '04', title: 'Independent Quality Assurance', desc: 'A second, independent linguist performs a full review of the edited translation against the source text, verifying compliance with ISO 17100 standards.' },
  { num: '05', title: 'Final Delivery & Feedback', desc: 'The finalized, certified translations are delivered in your desired format, and we incorporate your feedback into our systems for continuous improvement.' },
];

interface CaseStudyPDF {
  id: string;
  title: string;
  category: string;
  description: string;
  pages: number;
  language: string;
  pdfFile: string;
  highlight: string;
}

const caseStudyPDFs: CaseStudyPDF[] = [
  {
    id: 'consumer-electronics',
    title: 'Case Study: Consumer Electronics Global Localization',
    category: 'Consumer Electronics',
    description: 'How Into23 delivered millions of words annually across 30+ languages for leading consumer electronics brands with 100% on-time delivery.',
    pages: 5,
    language: 'English',
    pdfFile: '/whitepapers/Case_Study_Global_Content_Localization_Consumer_Electronics_Brands.pdf',
    highlight: '100% On-Time Delivery',
  },
  {
    id: 'life-sciences-elearning',
    title: 'Case Study: Life Sciences eLearning Localization',
    category: 'Life Sciences',
    description: 'AI-led multilingual localization of regulated eLearning content achieved 60% faster timelines and 50% cost savings without sacrificing scientific accuracy.',
    pages: 6,
    language: 'English',
    pdfFile: '/whitepapers/Case_Study_Transforming_Life_Science_eLearning.pdf',
    highlight: '60% Faster Timelines',
  },
  {
    id: 'legal-translation',
    title: 'Case Study: Large-Scale Legal Translation',
    category: 'Legal',
    description: 'Over 1 million words of Chinese-to-English legal content converted from complex PDFs into accurate, editable Word files in just 7 days.',
    pages: 4,
    language: 'English',
    pdfFile: '/whitepapers/Case_Study_Large_Scale_Legal_Translation_Document_Engineering.pdf',
    highlight: '1M+ Words in 7 Days',
  },
  {
    id: 'ecommerce-localization',
    title: 'Case Study: Multilingual eCommerce Localization',
    category: 'E-Commerce',
    description: 'High-volume eCommerce localization across 20+ languages with 99% first-delivery acceptance rate and 99% on-time delivery for every market.',
    pages: 5,
    language: 'English',
    pdfFile: '/whitepapers/Case_Study_Multilingual_Localization_for_eCommerce.pdf',
    highlight: '99% First-Delivery Acceptance',
  },
  {
    id: 'dataforce-transcription',
    title: 'Case Study: DataForce Multilingual Transcription',
    category: 'AI Data',
    description: 'Multilingual AI data operations supporting review and correction of AI-generated transcriptions across 25 languages with 10 reviewers per language.',
    pages: 4,
    language: 'English',
    pdfFile: '/whitepapers/DataForce_Transcription_Case_Study.pdf',
    highlight: '25 Languages Covered',
  },
  {
    id: 'sify-voiceover',
    title: 'Case Study: Sify Digital Multilingual eLearning Voiceover',
    category: 'Voiceover',
    description: 'Multilingual voiceover for global eLearning courses across 18 languages with 97% first-delivery acceptance and consistent audio quality.',
    pages: 5,
    language: 'English',
    pdfFile: '/whitepapers/Sify_Digital_Voiceover_Case_Study.pdf',
    highlight: '97% First-Delivery Acceptance',
  },
  {
    id: 'voice-data-ai',
    title: 'Case Study: Voice Data for AI Training',
    category: 'AI Data',
    description: 'High-fidelity multilingual voice recordings across 20 languages meeting strict technical requirements and ethical consent standards for speech-model training.',
    pages: 6,
    language: 'English',
    pdfFile: '/whitepapers/Case_Study_Voiceover_for_AI_Data_Training.pdf',
    highlight: '20 Languages, Auditable Consent',
  },
];

const faqs = [
  {
    q: 'How do you ensure the accuracy of life sciences translations?',
    a: 'We ensure accuracy through a multi-layered, ISO 17100-compliant process that combines advanced AI with expert human oversight. Every project begins with our AI, which is trained on vast, domain-specific datasets. This output is then meticulously reviewed and refined by a professional linguist with a background in the specific life sciences field. A second, independent linguist then conducts a final quality assurance check to guarantee the highest level of precision. This TEP (Translation, Editing, Proofreading) workflow, augmented by technology, is the cornerstone of our commitment to delivering clinically and regulatorily sound translations you can trust implicitly.',
  },
  {
    q: 'Can you handle translations for regulatory submissions to different APAC authorities?',
    a: 'Yes, we specialize in handling translations for regulatory submissions across diverse APAC authorities. Our teams in Hong Kong, Beijing, Shanghai, Bangalore, and Tokyo maintain up-to-date knowledge of the specific formatting, terminology, and submission requirements for bodies like China\'s NMPA, Japan\'s PMDA, and others. We understand that a one-size-fits-all approach is insufficient for regulatory success. Therefore, we adapt our processes and deliverables to align with local expectations, helping you navigate the complex and varied regulatory landscape of the Asia-Pacific region efficiently and effectively, minimizing delays and queries from health authorities.',
  },
  {
    q: 'What is your process for selecting translators for medical device documentation?',
    a: 'Our process for selecting translators for medical device documentation is exceptionally rigorous to ensure both technical accuracy and regulatory compliance. We only assign these projects to linguists who possess demonstrated experience in the medical device industry, often holding degrees in biomedical engineering or related fields. Before joining a project, candidates are vetted for their understanding of relevant standards like the EU MDR and ISO 13485. This ensures they are proficient not just in language, but in the specific terminology of instructions for use (IFUs), manufacturing processes, and software interfaces, guaranteeing a precise and compliant final product.',
  },
  {
    q: 'How do you maintain patient confidentiality and data security?',
    a: 'We maintain the highest standards of confidentiality and data security through a combination of robust technical infrastructure and strict procedural controls, with our ISO 27001 certification in progress. All project data is handled within a secure, encrypted environment with controlled access limited to essential personnel. Our global team members are bound by stringent non-disclosure agreements and receive regular training on data protection protocols, including HIPAA for US-related projects. We utilize secure file transfer protocols and can work within client-side platforms to ensure sensitive patient information never leaves your control, providing complete peace of mind.',
  },
  {
    q: 'What makes your AI translation different from standard machine translation?',
    a: 'Our AI translation is fundamentally different from standard machine translation because it is highly specialized and always human-verified. Unlike generic, off-the-shelf tools, our neural machine translation (NMT) engines are custom-trained on curated, high-quality corpora of life sciences content, including clinical trial data and regulatory filings. This specialization results in far greater terminological accuracy from the start. Most importantly, the AI output is never the final product. It serves as a high-quality draft for our human medical experts to edit and perfect, ensuring the nuance, context, and precision required for critical medical content is fully captured.',
  },
];

export default function LifeSciencesTranslationPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<CaseStudyPDF | null>(null);

  const openModal = (cs: CaseStudyPDF) => {
    setSelectedCaseStudy(cs);
    setModalOpen(true);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: obsidian }}>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 60% 0%, rgba(43,98,239,0.15) 0%, transparent 70%)' }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-6 text-sm" style={{ color: silverMuted }}>
            <Link href="/services" style={{ color: cobalt }}>Services</Link>
            <span>/</span>
            <span>Life Sciences Translation</span>
          </div>
          <span className="font-mono text-sm tracking-widest uppercase mb-4 block" style={{ color: cobalt }}>Translation</span>
          <h1 className="font-display font-bold text-4xl lg:text-6xl leading-tight mb-6" style={{ color: silverText }}>
            Life Sciences<br />
            <span style={{ color: cobaltGlow }}>Translation</span>
          </h1>
          <p className="text-lg lg:text-xl max-w-2xl mb-4 leading-relaxed" style={{ color: silverMuted }}>
            Precision, compliance, and expertise for the most demanding medical and pharmaceutical content.
          </p>
          <p className="text-base max-w-3xl mb-10 leading-relaxed" style={{ color: silverMuted }}>
            Into23 provides specialized translation and localization services for the life sciences sector, ensuring your clinical trial documentation, regulatory submissions, and patient-facing materials meet the stringent requirements of APAC markets. Our AI-powered, human-verified workflow combines cutting-edge technology with subject matter experts who understand the nuances of medical and pharmaceutical content. We are ISO 9001 &amp; 17100 certified, guaranteeing a rigorous quality assurance process that you can trust for your most critical projects.
          </p>
          <div className="flex flex-wrap gap-4 mb-14">
            <Link href="/get-a-quote?contentType=Medical%2FPharma">
              <div className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg text-white font-semibold transition-all hover:opacity-90" style={{ background: cobalt }}>
                Get a Quote <ArrowRightIcon />
              </div>
            </Link>
            <Link href="/resources">
              <div className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg font-semibold transition-all border" style={{ color: silverText, borderColor: 'rgba(85,136,251,0.3)' }}>
                View Resources
              </div>
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl p-5 border" style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(85,136,251,0.15)' }}>
                <div className="font-display font-bold text-3xl mb-1" style={{ color: cobaltGlow }}>{s.value}</div>
                <div className="font-semibold text-sm mb-0.5" style={{ color: silverText }}>{s.label}</div>
                <div className="text-xs" style={{ color: silverMuted }}>{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Deliver */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="mb-12">
            <span className="font-mono text-sm tracking-widest uppercase mb-3 block" style={{ color: cobalt }}>Capabilities</span>
            <h2 className="font-display font-bold text-3xl lg:text-4xl" style={{ color: silverText }}>What We Deliver</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {deliverables.map((d) => (
              <div key={d.title} className="rounded-xl p-6 border" style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(85,136,251,0.12)' }}>
                <div className="flex items-start gap-3">
                  <CheckIcon />
                  <div>
                    <h3 className="font-semibold mb-2" style={{ color: silverText }}>{d.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: silverMuted }}>{d.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 lg:py-28" style={{ background: 'rgba(43,98,239,0.04)' }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="mb-12">
            <span className="font-mono text-sm tracking-widest uppercase mb-3 block" style={{ color: cobalt }}>Our Process</span>
            <h2 className="font-display font-bold text-3xl lg:text-4xl" style={{ color: silverText }}>How It Works</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {steps.map((step) => (
              <div key={step.num} className="rounded-xl p-6 border" style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(85,136,251,0.12)' }}>
                <div className="font-mono font-bold text-2xl mb-3" style={{ color: cobalt }}>{step.num}</div>
                <h3 className="font-semibold mb-2 text-sm" style={{ color: silverText }}>{step.title}</h3>
                <p className="text-xs leading-relaxed" style={{ color: silverMuted }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies - Gated Downloads */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="mb-12">
            <span className="font-mono text-sm tracking-widest uppercase mb-3 block" style={{ color: cobalt }}>Case Studies</span>
            <h2 className="font-display font-bold text-3xl lg:text-4xl mb-3" style={{ color: silverText }}>
              Download Our Case Studies
            </h2>
            <p className="text-base max-w-2xl" style={{ color: silverMuted }}>
              Real results from real projects. Download any case study to see how Into23 delivers measurable outcomes across industries.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {caseStudyPDFs.map((cs) => (
              <div
                key={cs.id}
                className="rounded-xl p-6 border flex flex-col group cursor-pointer transition-all hover:border-cobalt/40"
                style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(85,136,251,0.12)' }}
                onClick={() => openModal(cs)}
              >
                {/* Icon + Category */}
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'rgba(43,98,239,0.15)', border: '1px solid rgba(43,98,239,0.25)' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={cobaltGlow} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                    </svg>
                  </div>
                  <span className="px-2.5 py-1 rounded-full text-xs font-mono" style={{ backgroundColor: 'rgba(43,98,239,0.1)', color: cobaltGlow, border: '1px solid rgba(43,98,239,0.2)' }}>
                    {cs.category}
                  </span>
                </div>

                {/* Title */}
                <h3 className="font-semibold text-base mb-2 leading-snug group-hover:text-cobalt-glow transition-colors" style={{ color: silverText }}>
                  {cs.title}
                </h3>

                {/* Description */}
                <p className="text-sm leading-relaxed mb-4 flex-1" style={{ color: silverMuted }}>
                  {cs.description}
                </p>

                {/* Metadata row */}
                <div className="flex items-center gap-3 mb-4 text-xs" style={{ color: silverMuted }}>
                  <span>PDF</span>
                  <span style={{ color: 'rgba(85,136,251,0.4)' }}>·</span>
                  <span>{cs.pages} pages</span>
                  <span style={{ color: 'rgba(85,136,251,0.4)' }}>·</span>
                  <span>{cs.language}</span>
                </div>

                {/* Highlight stat */}
                <div className="pt-4 border-t mb-4" style={{ borderColor: 'rgba(85,136,251,0.1)' }}>
                  <span className="text-sm font-semibold" style={{ color: cobaltGlow }}>{cs.highlight}</span>
                </div>

                {/* CTA */}
                <div
                  className="flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all"
                  style={{ backgroundColor: 'rgba(43,98,239,0.12)', color: cobaltGlow, border: '1px solid rgba(43,98,239,0.2)' }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download PDF
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28" style={{ background: 'rgba(43,98,239,0.04)' }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="mb-12">
            <span className="font-mono text-sm tracking-widest uppercase mb-3 block" style={{ color: cobalt }}>Common Questions</span>
            <h2 className="font-display font-bold text-3xl lg:text-4xl" style={{ color: silverText }}>Frequently Asked Questions</h2>
          </div>
          <div className="max-w-3xl space-y-5">
            {faqs.map((faq) => (
              <div key={faq.q} className="rounded-xl p-6 border" style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(85,136,251,0.12)' }}>
                <h3 className="font-semibold mb-3" style={{ color: silverText }}>{faq.q}</h3>
                <p className="text-sm leading-relaxed" style={{ color: silverMuted }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-3xl lg:text-5xl mb-6" style={{ color: silverText }}>
            Ready to Get Started?
          </h2>
          <p className="text-lg mb-10 max-w-xl mx-auto" style={{ color: silverMuted }}>
            Get a custom quote for your translation project. Our team typically responds within 24 hours.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link href="/get-a-quote?contentType=Medical%2FPharma">
              <div className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-white font-semibold transition-all hover:opacity-90" style={{ background: cobalt }}>
                Get a Quote <ArrowRightIcon size={18} />
              </div>
            </Link>
            <Link href="/services">
              <div className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-semibold transition-all border" style={{ color: silverText, borderColor: 'rgba(85,136,251,0.3)' }}>
                All Services
              </div>
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* Gated Download Modal */}
      <CaseStudyDownloadModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        caseStudy={selectedCaseStudy}
      />
    </div>
  );
}
